import {
  type DiscountDTO,
  type MinorUnit,
  type NexiOrderItem,
  type Option,
  type StorefrontOrderLine
} from 'ecosystem'
import {
  type BuyerVatRates,
  createMinorUnit,
  getTax,
  getVatRateByCountryCode,
  roundNumber,
  shippingForBuyer,
  storeGrossRebaser,
  sumArrayOfNumbers
} from 'shared-utils'
import { orderLineValueCalculator } from '../../payment'
import { getProductImages } from '../../products'

export const getNetPrice = (price: MinorUnit, taxRate: MinorUnit) => {
  return price - getTax(price, taxRate, true)
}

const createDiscountItem = ({
  name,
  totalDiscountAmount,
  taxRate,
  quantity
}: {
  name: string
  totalDiscountAmount: number
  taxRate: number
  quantity: number
}): NexiOrderItem => {
  const convertToNegative = (amount: number) => -Math.abs(amount)
  const unitPrice = totalDiscountAmount / quantity
  const unitNetPrice = getNetPrice(unitPrice, taxRate)

  return {
    reference: 'discount',
    name: `Discount ${name}`,
    quantity,
    unit: 'NA',
    unitPrice: convertToNegative(roundNumber(unitNetPrice, 0)),
    taxRate,
    taxAmount: convertToNegative(roundNumber(getTax(totalDiscountAmount, taxRate, true), 0)),
    grossTotalAmount: convertToNegative(roundNumber(totalDiscountAmount, 0)),
    netTotalAmount: convertToNegative(roundNumber(unitNetPrice * quantity, 0))
  }
}

const mapOrderLinesToNexiOrderItems = (
  items: StorefrontOrderLine[],
  cartDiscountCode: DiscountDTO | null
): NexiOrderItem[] => {
  return items.reduce<NexiOrderItem[]>((acc, item) => {
    const { product, quantity, vatRate } = item
    const taxRate = createMinorUnit(vatRate)
    const { unitPrice, totalDiscountAmount } = orderLineValueCalculator(item, cartDiscountCode)
    const { mainImageUrl } = getProductImages(product.medias)

    const unitNetPrice = getNetPrice(unitPrice, taxRate)

    acc.push({
      reference: product.articleNumber,
      name: product.name,
      quantity,
      unit: 'pcs',
      unitPrice: roundNumber(unitNetPrice, 0),
      taxRate,
      taxAmount: roundNumber(getTax(unitPrice * quantity, taxRate, true), 0),
      grossTotalAmount: roundNumber(unitPrice * quantity, 0),
      netTotalAmount: roundNumber(unitNetPrice * quantity, 0),
      imageUrl: mainImageUrl
    })

    if (totalDiscountAmount) {
      acc.push(createDiscountItem({ name: product.name, totalDiscountAmount, taxRate, quantity }))
    }

    return acc
  }, [])
}

const servicesOrderLineFactory = (
  items: StorefrontOrderLine[],
  taxRate: MinorUnit,
  rebaseServicePrice: (grossMajor: number) => number
): NexiOrderItem[] => {
  return items
    .map((item) =>
      item.services.map((service) => {
        const quantity = service.quantity
        let unitPrice = createMinorUnit(rebaseServicePrice(service.service.price))
        const discountFactor = createMinorUnit(rebaseServicePrice(service.service.discountPrice))
        let grossTotalAmount = 0

        /*
         Basic formula for calculating price of service:
         service.price + service.discountPrice * (quantity - 1)
         */
        if (quantity > 0) {
          grossTotalAmount = unitPrice + (quantity - 1) * discountFactor

          if (!unitPrice && discountFactor) {
            unitPrice = discountFactor
          }
        }

        return {
          reference: service.service.id,
          name: service.service.name,
          quantity,
          unit: 'service',
          unitPrice: roundNumber(getNetPrice(unitPrice, taxRate), 0),
          taxRate,
          taxAmount: roundNumber(getTax(grossTotalAmount, taxRate, true), 0),
          grossTotalAmount: roundNumber(grossTotalAmount, 0),
          netTotalAmount: roundNumber(getNetPrice(grossTotalAmount, taxRate), 0)
        }
      })
    )
    .flat()
}

export const nexiOrderLineFactory = (
  items: StorefrontOrderLine[],
  cartDiscountCode: DiscountDTO | null,
  countryCode?: string,
  buyerVat?: Option<BuyerVatRates>
) => {
  // Product rows carry each line's own `vatRate`, already resolved for the
  // buyer upstream. Service rows have no rate of their own, so they take the
  // store's rate — resolved here for the same buyer so the two agree.
  const domesticRate = getVatRateByCountryCode(countryCode)
  const taxRate = createMinorUnit(buyerVat?.resolveRate(domesticRate) ?? domesticRate)
  const productOrderLines = mapOrderLinesToNexiOrderItems(items, cartDiscountCode)
  const serviceOrderLines = servicesOrderLineFactory(
    items,
    taxRate,
    storeGrossRebaser(domesticRate, buyerVat)
  )
  const orderLines = [...productOrderLines, ...serviceOrderLines]
  const orderAmount = sumArrayOfNumbers(
    orderLines.map((item: NexiOrderItem) => item.grossTotalAmount)
  )
  // The same basket net of VAT, in the units `orderAmount` uses. Feeds
  // `netOrderAmount` on the shipping factories so a delivery rate configured
  // ex-VAT is evaluated against the figure it was written on. Derived from the
  // rows rather than from the basket, so the discount code and this provider's
  // unit convention are already applied.
  const netOrderAmount = sumArrayOfNumbers(
    orderLines.map((item: NexiOrderItem) => item.netTotalAmount)
  )
  return {
    orderLines,
    orderAmount,
    netOrderAmount,
    taxRate,
    /** Move a carrier's VAT-inclusive quote onto this buyer's rate. Unit-agnostic
     *  (a pure ratio), so minor units pass through unchanged. */
    rebaseShipping: (grossQuote: number) =>
      shippingForBuyer(grossQuote, domesticRate, buyerVat).cost
  }
}
